import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useCookieOptions } from '~/composables/useCookieOptions';
import { STORAGE_KEYS } from '~/constants/storageKeys';

export const useAuthStore = defineStore('auth', () => {
  const token = ref('');
  const refreshToken = ref('');
  const csrfToken = ref('');

  // 使用統一的 cookie 選項，確保 domain 設定正確
  // 7天過期時間，確保持久性
  // 注意：不設定 default，避免在沒有值時自動創建空值 cookie
  // 只有在明確設定值時才會創建 cookie
  const cookieOptions = useCookieOptions<string>({
    maxAge: 60 * 60 * 24 * 7, // 7天 (以秒為單位)
  });

  // 使用 useCookie，不設定 default，避免自動創建空值 cookie
  // 只有在明確設定值時才會創建 cookie
  const tokenCookie = useCookie<string>(STORAGE_KEYS.TOKEN, cookieOptions);
  const refreshTokenCookie = useCookie<string>(STORAGE_KEYS.REFRESH_TOKEN, cookieOptions);

  const getToken = () => {
    if (isLoggedIn()) return token.value;
    return '';
  };

  const getRefreshToken = () => {
    if (isLoggedIn()) return refreshToken.value;
    return '';
  };

  const getCsrfToken = () => {
    if (csrfToken.value) {
      return csrfToken.value;
    }
    if (import.meta.client) {
      const storedToken = localStorage.getItem(STORAGE_KEYS.CSRF_TOKEN);
      if (storedToken) {
        csrfToken.value = storedToken;
        return storedToken;
      }
    }
    return '';
  };

  const isLoggedIn = () => {
    // 檢查 store 中是否有 token 和 refreshToken
    if (token.value && refreshToken.value) {
      return true;
    }

    // 檢查 cookies 中是否有 token 和 refreshToken（從之前的登入保存的）
    // 如果 cookie 存在且有值，則使用它
    if (tokenCookie.value && refreshTokenCookie.value) {
      token.value = tokenCookie.value;
      refreshToken.value = refreshTokenCookie.value;
      return true;
    }

    return false;
  };

  /**
   * 設定 token 和 refreshToken
   * Cookie 的 domain 會根據當前環境自動設定：
   * - localhost 環境：domain 為空（使用當前域名）
   * - 生產環境（app.momentx.ai）：domain 為 .momentx.ai
   */
  const setTokens = (_token: string, _refreshToken: string | null) => {
    // 更新 store 中的值
    token.value = _token;
    if (_refreshToken) {
      refreshToken.value = _refreshToken;
    }

    // 更新 cookie（domain 會根據當前 hostname 自動設定）
    // 只有在有值時才設定 cookie，避免創建空值 cookie
    if (_token) {
      tokenCookie.value = _token;
    }
    if (_refreshToken) {
      refreshTokenCookie.value = _refreshToken;
    }
  };

  const setCsrfToken = (_csrfToken: string) => {
    csrfToken.value = _csrfToken;
    if (import.meta.client) {
      if (_csrfToken) {
        localStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, _csrfToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
      }
    }
  };

  const clearCsrfToken = () => {
    csrfToken.value = '';
    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
    }
  };

  const clearTokens = () => {
    token.value = '';
    refreshToken.value = '';
    csrfToken.value = '';
    // 清除 cookie：設定為空字串來清除 cookie
    // Nuxt 的 useCookie 會將空字串視為清除 cookie
    if (tokenCookie.value) {
      tokenCookie.value = '';
    }
    if (refreshTokenCookie.value) {
      refreshTokenCookie.value = '';
    }

    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
    }

    // 在不同環境下嘗試清除可能的 cookies
    if (import.meta.client) {
      const hostname = window.location.hostname;

      // 取得根域名的輔助函數
      const getRootDomain = (host: string): string => {
        if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
          return '';
        }
        const parts = host.split('.');
        if (parts.length >= 2) {
          return `.${parts.slice(-2).join('.')}`;
        }
        return '';
      };

      const rootDomain = getRootDomain(hostname);

      // 清除當前域名的 cookies（不設定 domain，適用於 localhost）
      document.cookie = `${STORAGE_KEYS.TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${STORAGE_KEYS.REFRESH_TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${STORAGE_KEYS.ORGUUID}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

      // 如果有根域名（生產環境），也嘗試清除跨域 cookies
      if (rootDomain) {
        document.cookie = `${STORAGE_KEYS.TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain}`;
        document.cookie = `${STORAGE_KEYS.REFRESH_TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain}`;
        document.cookie = `${STORAGE_KEYS.ORGUUID}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain}`;
      }
    }
  };

  const reset = () => {
    clearTokens();
  };

  return {
    token,
    refreshToken,
    csrfToken,
    getToken,
    getRefreshToken,
    getCsrfToken,
    isLoggedIn,
    setTokens,
    setCsrfToken,
    clearTokens,
    clearCsrfToken,
    reset,
  };
});
